import { useTranslation } from 'next-i18next';
import { ReactNode, useEffect, useRef } from 'react';
import { styled } from 'styled-components';
import ImageWrapper from '../../../new-components/ImageWrapper';
import { gsap } from 'gsap';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';

const Chip = ({
  children,
  className,
  chipRef
}: {
  children: ReactNode;
  className: string;
  chipRef?: React.RefObject<HTMLSpanElement>;
}) => {
  const router = useRouter();
  return (
    <ChipContainer
      className={className}
      ref={chipRef}
      locale={router.locale || 'en'}
    >
      {children}
    </ChipContainer>
  );
};

const AreasChip = ({ animate }: { animate: boolean }) => {
  const { t } = useTranslation('common');
  const chipRef = useRef<HTMLSpanElement>(null);
  const isMobile = useSelector((state: any) => state.app.isMobile);

  useEffect(() => {
    if (animate) {
      gsap.to(chipRef.current, {
        opacity: 0,
        ...(!isMobile && { x: -40 }),
        ...(isMobile && { y: 40 }),
        pointerEvents: 'none',
        duration: 1,
        ease: 'power2.inOut'
      });
    } else {
      gsap.to(chipRef.current, {
        opacity: 1,
        x: 0,
        y: 0,
        pointerEvents: 'auto',
        duration: 1,
        ease: 'power2.inOut'
      });
    }
  }, [animate]);

  useEffect(() => {
    if (isMobile) {
      gsap.fromTo(
        chipRef.current,
        {
          x: 100
        },
        {
          x: 0,
          duration: 1.2,
          ease: 'back.out(3)'
        }
      );
    }
  }, []);

  return (
    <Chip className="area-chip" chipRef={chipRef}>
      <span className="semi-bold">100+</span>
      <span className="regular">{t('compounds')}</span>
    </Chip>
  );
};

const PropertiesChip = ({ animate }: { animate: boolean }) => {
  const { t } = useTranslation('common');
  const chipRef = useRef<HTMLSpanElement>(null);
  const isMobile = useSelector((state: any) => state.app.isMobile);
  useEffect(() => {
    if (animate) {
      gsap.to(chipRef.current, {
        ...(!isMobile && { top: '50%' }),
        ...(!isMobile && { left: 'calc(50% - 220px)' }),
        ...(isMobile && { y: 40 }),
        ...(isMobile && { opacity: 0 }),
        duration: 1,
        ease: 'power2.inOut'
      });
    } else {
      gsap.to(chipRef.current, {
        top: isMobile ? -100 : '-20vh',
        ...(!isMobile && { left: 'calc(50% + 100px)' }),
        ...(isMobile && { y: 0 }),
        opacity: 1,
        duration: 1,
        ease: 'power2.inOut'
      });
    }
  }, [animate]);

  useEffect(() => {
    if (isMobile) {
      gsap.fromTo(
        chipRef.current,
        {
          x: 100
        },
        {
          x: 0,
          duration: 1.2,
          ease: 'back.out(3)'
        }
      );
    }
  }, []);

  return (
    <Chip className="properties-chip" chipRef={chipRef}>
      <div className="icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={isMobile ? 20 : 34}
          height={isMobile ? 20 : 34}
          viewBox="0 0 34 34"
          fill="none"
        >
          <path
            d="M6.51683 15.5833H10.6252V8.78333C10.6252 7.19652 10.6252 6.40311 10.934 5.79703C11.2056 5.2639 11.6391 4.83046 12.1722 4.55881C12.7783 4.25 13.5717 4.25 15.1585 4.25H18.8418C20.4286 4.25 21.2221 4.25 21.8281 4.55881C22.3613 4.83046 22.7947 5.2639 23.0663 5.79703C23.3752 6.40311 23.3752 7.19652 23.3752 8.78333V15.5833H27.4835C28.2769 15.5833 28.6736 15.5833 28.9766 15.7377C29.2432 15.8736 29.4599 16.0903 29.5958 16.3568C29.7502 16.6599 29.7502 17.0566 29.7502 17.85V29.75H23.3752H10.6252H4.25016V17.85C4.25016 17.0566 4.25016 16.6599 4.40457 16.3568C4.54039 16.0903 4.75711 15.8736 5.02368 15.7377C5.32672 15.5833 5.72342 15.5833 6.51683 15.5833Z"
            fill="#212431"
          />
          <path
            d="M10.6252 15.5833H6.51683C5.72342 15.5833 5.32672 15.5833 5.02368 15.7377C4.75711 15.8736 4.54039 16.0903 4.40457 16.3568C4.25016 16.6599 4.25016 17.0566 4.25016 17.85V29.75M23.3752 15.5833H27.4835C28.2769 15.5833 28.6736 15.5833 28.9766 15.7377C29.2432 15.8736 29.4599 16.0903 29.5958 16.3568C29.7502 16.6599 29.7502 17.0566 29.7502 17.85V29.75M23.3752 29.75V8.78333C23.3752 7.19652 23.3752 6.40311 23.0663 5.79703C22.7947 5.2639 22.3613 4.83046 21.8281 4.55881C21.2221 4.25 20.4286 4.25 18.8418 4.25H15.1585C13.5717 4.25 12.7783 4.25 12.1722 4.55881C11.6391 4.83046 11.2056 5.2639 10.934 5.79703C10.6252 6.40311 10.6252 7.19652 10.6252 8.78333V29.75M31.1668 29.75H2.8335M15.5835 9.91667H18.4168M15.5835 15.5833H18.4168M15.5835 21.25H18.4168M15.5835 9.91667H18.4168M15.5835 15.5833H18.4168M15.5835 21.25H18.4168"
            stroke="#212431"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M15.5835 9.91797H18.4168M15.5835 15.5846H18.4168M15.5835 21.2513H18.4168"
            stroke="#F9F9F9"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
      <div className="info">
        <span className="semi-bold">1,000+</span>
        <span className="regular">{t('properties')}</span>
      </div>
    </Chip>
  );
};

const DeveloperChip = ({
  developerLogos,
  animate
}: {
  developerLogos: string[];
  animate: boolean;
}) => {
  const { t } = useTranslation('common');
  const chipRef = useRef<HTMLSpanElement>(null);
  const isMobile = useSelector((state: any) => state.app.isMobile);
  useEffect(() => {
    if (animate) {
      gsap.to(chipRef.current, {
        opacity: 0,
        y: isMobile ? 40 : -40,
        pointerEvents: 'none',
        duration: 1,
        ease: 'power2.inOut'
      });
    } else {
      gsap.to(chipRef.current, {
        opacity: 1,
        y: 0,
        pointerEvents: 'auto',
        duration: 1,
        ease: 'power2.inOut'
      });
    }
  }, [animate]);

  useEffect(() => {
    if (isMobile) {
      gsap.fromTo(
        chipRef.current,
        {
          x: -100
        },
        {
          x: 0,
          duration: 1.2,
          ease: 'back.out(3)'
        }
      );
    }
  }, []);

  return (
    <Chip className="developer-chip" chipRef={chipRef}>
      <div className="info">
        <span className="semi-bold">30+</span>
        <span className="regular">{t('developers')}</span>
      </div>
      <div className="logos">
        {developerLogos.map((logo, index) => (
          <div
            className="logo-wrapper"
            key={index}
            style={{
              insetInlineStart: `${isMobile ? index * 18 : index * 25}px`,
              zIndex: developerLogos.length + index
            }}
          >
            <ImageWrapper
              src={logo}
              alt={`Developer Logo ${index + 1}`}
              className="developer-logo"
              fill
            />
          </div>
        ))}
      </div>
    </Chip>
  );
};

const Chips = ({
  animate,
  developerLogos
}: {
  animate: boolean;
  developerLogos: Array<{ logo: string }>;
}) => {
  return (
    <>
      <AreasChip animate={animate} />
      <PropertiesChip animate={animate} />
      <DeveloperChip
        animate={animate}
        developerLogos={developerLogos.map(dev => dev.logo)}
      />
    </>
  );
};

const ChipContainer = styled.span<{ locale?: string }>`
  border-radius: 16px;
  background: rgba(230, 230, 230, 0.38);
  backdrop-filter: blur(7px);
  color: #212431;
  width: fit-content;
  position: absolute;
  z-index: 1111;
  span {
    font-size: 20px;
    line-height: 140%;
    &.regular {
      font-family: materialRegular;
    }

    &.medium {
      font-family: materialMedium;
    }

    &.bold {
      font-family: materialBold;
    }

    &.semi-bold {
      font-family: materialSemiBold;
    }
  }

  &.area-chip {
    display: flex;
    gap: 8px;
    padding: 12px 24px;
    top: -60px;
    right: calc(50% - 480px);
  }
  &.properties-chip {
    display: flex;
    align-items: center;
    gap: 22px;
    padding: 16px;
    width: 212px;
    top: -20vh;
    left: calc(50% + 100px);

    .icon {
      width: 57px;
      min-width: 57px;
      height: 57px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(255, 255, 255, 0.7);
      border-radius: 50%;
    }
    .info {
      display: flex;
      flex-direction: column;
      span {
        line-height: 140%;
      }
    }
  }

  &.developer-chip {
    display: flex;
    flex-direction: column;
    padding: 16px;
    top: -15vh;
    left: calc(50% - 300px);
    min-width: 200px;
    .info {
      display: flex;
      flex-direction: row;
      gap: 4px;
    }
    .logos {
      position: relative;
      height: 46px;
      min-width: 56px;
      .logo-wrapper {
        width: 46px;
        height: 46px;
        position: absolute;
        inset-inline-start: 0;
        .developer-logo {
          box-shadow: 0px 0px 4.385px 0px rgba(0, 0, 0, 0.06);
          border: 1px solid #f1f1f1;
          border-radius: 50%;
        }
      }
    }
  }

  @media screen and (max-width: 768px) {
    border-radius: 12px;
    span {
      font-size: 14px;
    }
    &.area-chip {
      gap: 4px;
      padding: 6px 8px;
      top: -40px;
      right: ${props => (props.locale == 'ar' ? 'unset' : '20px')};
      left: ${props => (props.locale == 'ar' ? '20px' : 'unset')};
    }
    &.properties-chip {
      gap: 8px;
      padding: 8px;
      width: 130px;
      top: -100px;
      right: ${props => (props.locale == 'ar' ? 'unset' : '110px')};
      left: ${props => (props.locale == 'ar' ? '110px' : 'unset')};
      .icon {
        min-width: 34px;
        width: 34px;
        height: 34px;
      }
    }

    &.developer-chip {
      padding: 8px;
      top: -40px;
      left: ${props => (props.locale == 'ar' ? 'unset' : '20px')};
      right: ${props => (props.locale == 'ar' ? '20px' : 'unset')};
      min-width: 140px;
      .logos {
        height: 28px;
        min-width: 40px;
        .logo-wrapper {
          width: 28px;
          height: 28px;
        }
      }
    }
  }
`;

export default Chips;
